import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { ActivityArchiveResponse } from "@learn-flow/contracts";

import { calendarQueryRoot } from "../calendar/calendar-api.js";
import { todayQueryRoot } from "../today/today-api.js";
import { libraryQueryRoot, setActivityArchived } from "./library-api.js";

type ArchiveActivity = {
  id: string;
  title: string;
};

export function ArchiveActivityDialog({
  activity,
  onArchived,
  onDismiss,
}: {
  activity: ArchiveActivity;
  onArchived?: (result: ActivityArchiveResponse) => void | Promise<void>;
  onDismiss: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => setActivityArchived(activity.id, "archive"),
    onSuccess: async (result) => {
      await onArchived?.(result);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: libraryQueryRoot }),
        queryClient.invalidateQueries({ queryKey: todayQueryRoot }),
        queryClient.invalidateQueries({ queryKey: calendarQueryRoot }),
      ]);
      dialog.current?.close();
    },
  });
  useModalDialog(dialog, onDismiss);

  return (
    <dialog
      ref={dialog}
      className="calm-dialog"
      aria-labelledby="archive-activity-dialog-title"
      aria-describedby="archive-activity-dialog-description"
      onCancel={(event) => {
        if (mutation.isPending) event.preventDefault();
      }}
    >
      <div>
        <span className="section-kicker">Archive lesson</span>
        <h2 id="archive-activity-dialog-title">Archive “{activity.title}”?</h2>
        <p id="archive-activity-dialog-description">
          Any incomplete sessions scheduled for today or a future date will be removed from Today
          and Calendar. Restoring this lesson will not recreate those sessions.
        </p>
        {mutation.isError ? (
          <p className="field-error" role="alert">
            The lesson could not be archived. Nothing was changed. Please try again.
          </p>
        ) : null}
        <div className="dialog-actions">
          <button
            type="button"
            className="soft-button"
            disabled={mutation.isPending}
            onClick={() => dialog.current?.close()}
          >
            Keep lesson
          </button>
          <button
            type="button"
            className="danger-button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Archiving…" : mutation.isError ? "Try again" : "Archive lesson"}
          </button>
        </div>
      </div>
    </dialog>
  );
}

function useModalDialog(
  reference: React.RefObject<HTMLDialogElement | null>,
  onDismiss: () => void,
): void {
  const dismiss = useRef(onDismiss);
  const opener = useRef<HTMLElement | null>(null);
  dismiss.current = onDismiss;

  useEffect(() => {
    const dialog = reference.current;
    if (!dialog) return;
    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (activeElement && !dialog.contains(activeElement)) {
      opener.current = activeElement;
    }
    let restoredFocus = false;
    const restoreFocus = () => {
      if (restoredFocus) return;
      restoredFocus = true;
      window.requestAnimationFrame(() => {
        if (opener.current?.isConnected) opener.current.focus();
      });
    };
    const handleClose = () => {
      dismiss.current();
      restoreFocus();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]",
        ),
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.showModal();
    dialog.addEventListener("close", handleClose);
    dialog.addEventListener("keydown", handleKeyDown);
    return () => {
      dialog.removeEventListener("close", handleClose);
      dialog.removeEventListener("keydown", handleKeyDown);
    };
  }, [reference]);
}
